import { getAuthBaseUrl } from '../auth/getAuthBaseUrl';

export type UserProfile = {
  username: string;
  email: string;
  fullName: string;
  avatar: string;
};

export const EMPTY_PROFILE: UserProfile = {
  username: '',
  email: '',
  fullName: '',
  avatar: '',
};

const PROFILE_EVENT = 'lk-meet-profile';

let cachedProfile: UserProfile = { ...EMPTY_PROFILE };

export function getCachedProfile(): UserProfile {
  return { ...cachedProfile };
}

export function setCachedProfile(profile: UserProfile | null) {
  cachedProfile = profile ? { ...profile } : { ...EMPTY_PROFILE };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PROFILE_EVENT));
  }
}

export function clearCachedProfile() {
  setCachedProfile(null);
}

export function subscribeProfile(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(PROFILE_EVENT, listener);
  return () => window.removeEventListener(PROFILE_EVENT, listener);
}

export function apiUserToProfile(user: {
  username: string;
  email?: string;
  full_name: string;
  avatar: string;
}): UserProfile {
  return {
    username: user.username ?? '',
    email: user.email ?? '',
    fullName: user.full_name ?? '',
    avatar: user.avatar ?? '',
  };
}

export function getInitials(fullName: string, username: string): string {
  const source = fullName.trim() || username.trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function avatarHue(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

export const AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

export function resolveAvatarUrl(avatar?: string | null): string {
  const value = avatar?.trim();
  if (!value) return '';
  if (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('blob:') ||
    value.startsWith('data:')
  ) {
    return value;
  }
  const base = getAuthBaseUrl();
  return value.startsWith('/') ? `${base}${value}` : `${base}/${value}`;
}

export function validateAvatarFile(file: File): string | null {
  const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  if (!allowed.includes(file.type)) {
    return 'Допустимы JPEG, PNG, WebP или GIF';
  }
  if (file.size > AVATAR_MAX_BYTES) {
    return 'Размер файла не больше 5 МБ';
  }
  return null;
}
